import type {
  ImpactField,
  ImpactQuery,
  QueryImpact,
  QueryImpactFilter,
  QueryImpactInput,
  QueryMembership,
} from '../../types/cache/query-impact.types';

const scalar = (value: unknown): string | undefined =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'
    ? String(value)
    : undefined;

const evaluateFilter = (
  filter: QueryImpactFilter,
  field: ImpactField
): QueryMembership => {
  if (!field.known) return 'unknown';
  if (filter.operator === 'exists' || filter.operator === 'missing') {
    const exists = field.value !== null && field.value !== undefined;
    return exists === (filter.operator === 'exists') ? 'match' : 'miss';
  }
  if (!['eq', 'in'].includes(filter.operator ?? '')) return 'unknown';
  const values = Array.isArray(filter.value) ? filter.value : [filter.value];
  if (
    (filter.operator === 'eq' && Array.isArray(filter.value)) ||
    values.some((value) => scalar(value) === undefined)
  ) {
    return 'unknown';
  }
  if (field.value == null) return 'miss';
  const actual = scalar(field.value);
  if (actual === undefined) return 'unknown';
  return values.some((value) => scalar(value) === actual) ? 'match' : 'miss';
};

/**
 * Evaluates only adapter-known scalar predicates. Full-text and unsupported
 * predicates remain unknown; a mandatory mismatch still proves exclusion.
 * @param query - Search semantics (AND filters plus an optional OR group).
 * @param item - Known model snapshot.
 * @param readField - Model-owned field knowledge, without guessed nested values.
 * @returns Proven membership, exclusion or uncertainty.
 */
export const evaluateQueryMembership = <T>(
  query: ImpactQuery,
  item: T,
  readField: (item: T, field: string) => ImpactField
): QueryMembership => {
  const mandatory: QueryMembership[] = [];
  const alternatives: QueryMembership[] = [];
  for (const filter of query.filters ?? []) {
    const result = filter.field
      ? evaluateFilter(filter, readField(item, filter.field))
      : 'unknown';
    (filter.or ? alternatives : mandatory).push(result);
  }
  if (mandatory.includes('miss')) return 'miss';
  if (alternatives.length && alternatives.every((value) => value === 'miss')) {
    return 'miss';
  }
  if (
    query.search?.trim() ||
    mandatory.includes('unknown') ||
    (alternatives.length && !alternatives.includes('match'))
  ) {
    return 'unknown';
  }
  return 'match';
};

/**
 * Classifies an update without inventing rows, count deltas or cursor positions.
 * @param input - Before/after model states and the owning query adapter.
 * @returns Safe existing-row actions and whether search reconciliation is needed.
 */
export const evaluateQueryImpact = <T>({
  query,
  before,
  after,
  readField,
  containsItem,
  needsEnrichment = false,
}: QueryImpactInput<T>): QueryImpact => {
  const previous =
    before === undefined
      ? 'unknown'
      : evaluateQueryMembership(query, before, readField);
  const next =
    after === undefined
      ? 'miss'
      : evaluateQueryMembership(query, after, readField);
  const sortChanged = (query.sort ?? []).some(({ field }) => {
    if (!field || before === undefined || after === undefined) return true;
    const oldValue = readField(before, field);
    const newValue = readField(after, field);
    return (
      !oldValue.known || !newValue.known || oldValue.value !== newValue.value
    );
  });
  const unaffected = previous === 'miss' && next === 'miss' && !containsItem;
  return {
    before: previous,
    after: next,
    patch: containsItem && next !== 'miss',
    remove: containsItem && next === 'miss',
    reconcile:
      !unaffected &&
      (previous !== 'match' ||
        next !== 'match' ||
        !containsItem ||
        needsEnrichment ||
        sortChanged),
  };
};
